import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { URLS } from "../../../_config";
import Container from "../../../components/Container";
import SiteButtons from "../../../components/SiteButtons";
import SubHeader from "../../../components/SubHeader";
import BodyGroupHeading from "../../../components/BodyGroupHeading";
import Icon from "../../../components/icons";
import ReusableTable from "../../../components/ReusableTable";
import { Controller, useForm } from "react-hook-form";
import Input from "../../../components/elements/form/input";
import { useCustomersApi } from "../../../store/api/hooks/customersApiHook";
import CommonDropdown from "../../../components/elements/form/commonDropdown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { useCommonApi } from "../../../store/api/hooks/commonApiHook";
import Loader from "../../../components/loader";
import { formatPhoneNumber, navigationConfirm } from "../../../_common/functions";
import { useDialog } from "../../../store/api/hooks/dialogHook";
import { useTranslation } from "react-i18next";
import moment from "moment";
import FormattedDate from "../../../components/elements/FormattedDate";

interface Props {
  type?: any;
  customergroup?: any;
}

type FormValues = {
  keyword: string;
  city: string;
  createdDateFrom: Date | null;
  createdDateTo: Date | null;
  locationIds: any[];
  groupIds: any[];
  status: any[];
};

const AddCustomerGroupManual: React.FC<Props> = ({
  type,
  customergroup = null,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("customers");

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      keyword: "",
      city: "",
      createdDateFrom: null,
      createdDateTo: null,
      locationIds: [],
      groupIds: [],
      status: [],
    },
  });

  // Separate states for customer group management
  const customerGroupNameRef = React.useRef<HTMLInputElement>(null);

  const [customerGroupName, setCustomerGroupName] = useState<string>("");
  const [customerGroupNameError, setCustomerGroupNameError] =
    useState<string>("");

  const [customerData, setCustomerData] = useState<any>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [selectedCustomers, setSelectedCustomers] = useState<Array<any>>([]);
  const [createdDateRange, setCreatedDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [isInitailSearchDone, setIsInitialSearchDone] =
    useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [lastSearchData, setLastSearchData] = useState<any>(null);

  const customersApi = useCustomersApi();
  const commonApi = useCommonApi();
  const {confirm} = useDialog();

  const searchCustomers = (data?: any, resetPagination: boolean = false, appendData: boolean = false) => {
    const currentPageToUse = resetPagination ? 1 : currentPage;

    // Include customer group ID in search params when in edit mode
    const searchParams = { ...data };
    if (customergroup?.groupId && !data?.customerGroupIds) {
      searchParams.customerGroupIds = [Number(atob(customergroup.groupId))];
    }

    // Determine page size: if we're at the last page and appending, use 20, otherwise use 10
    const isLastBatch = currentPageToUse * pageSize >= totalRows;
    const currentPageSize = appendData && isLastBatch && totalRows > 0 ? 20 : pageSize;

    const params = {
      ...searchParams,
      pageNumber: currentPageToUse,
      pageSize: currentPageSize,
    };

    // Store search data for infinite scroll
    if (!appendData) {
      setLastSearchData(searchParams);
    }

    if (resetPagination && currentPage !== 1) {
      setCurrentPage(1);
    }

    if (data && !appendData) {
      setSearching(true);
    }
    
    if (appendData) {
      setLoadingMore(true);
    }

    if (!appendData) {
      setCustomerData([]);
    }

    customersApi.searchCustomer(
      params,
      (response: any) => {
        if (!response?.success) {
          if (!appendData) {
            setCustomerData([]);
            setTotalRows(0);
          }
          setSearching(false);
          setLoadingMore(false);
          return;
        }

        const mappedCustomers = response.data?.items?.map(
          (customer: any, index: number) => ({
            ...customer,
            mobileAtob: customer.mobileNo ? atob(customer?.mobileNo) : "-",
            phoneAtob: customer.phoneNo ? atob(customer?.phoneNo) : "-",
            emailAtob: customer.email ? atob(customer?.email) : "-",
            idAtob: atob(customer?.customerId) || "",
            id: customer.customerId || `customer-${index}`,
            level: 0,
            hasChildren: false,
            expanded: false,
            parentId: null,
            isDragging: false,
            isDragOver: false,
          })
        );

        if (appendData) {
          // Append new data to existing data
          setCustomerData((prevData: any[]) => [...prevData, ...(mappedCustomers || [])]);
        } else {
          // Replace data
          if (mappedCustomers?.length > 0) {
            setCustomerData(mappedCustomers);
          } else {
            setCustomerData([]);
          }
        }

        setTotalRows(response.data.totalRows || 0);
        
        // Check if there's more data to load
        const currentTotalLoaded = appendData ? customerData.length + (mappedCustomers?.length || 0) : (mappedCustomers?.length || 0);
        setHasMore(currentTotalLoaded < (response.data.totalRows || 0));
        
        setSearching(false);
        setLoadingMore(false);
      },
      (error: any) => {
        setSearching(false);
        setLoadingMore(false);
        if (!appendData) {
          setCustomerData([]);
          setTotalRows(0);
        }
        console.log("error =>", error);
      }
    );
  };

  const resetSearch = () => {
    // Reset only search-related fields, preserve group name
    setValue("keyword", "");
    setValue("city", "");
    setValue("createdDateFrom", null);
    setValue("createdDateTo", null);
    setValue("locationIds", []);
    setValue("groupIds", []);
    setValue("status", []);
    setCreatedDateRange([null, null]);
    setCurrentPage(1);
    setHasMore(true);
    
    // In edit mode, reset but keep the customer group filter
    if (customergroup?.groupId) {
      setValue("groupIds", [{ 
        value: customergroup.groupId, 
        label: customergroup.groupName 
      }]);
      const searchData = {
        customerGroupIds: [Number(atob(customergroup.groupId))]
      };
      searchCustomers(searchData, true);
    } else {
      searchCustomers(undefined, true);
    }
  };

  const loadMoreCustomers = () => {
    if (hasMore && !loadingMore && !searching && lastSearchData) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      
      // Use the stored search data for the next page
      searchCustomers(lastSearchData, false, true);
    }
  };

  const handleScroll = (event: any) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold
    
    if (isNearBottom && hasMore && !loadingMore && !searching) {
      loadMoreCustomers();
    }
  };

  const handleCustomerSelection = (customer: any) => {
    const updatedSelection = [...selectedCustomers];
    const existingEntryIndex = updatedSelection.findIndex(
      (c) => c.fkCustomerId === customer.id
    );

    if (existingEntryIndex !== -1) {
      // Customer is already selected, remove it (deselect)
      updatedSelection.splice(existingEntryIndex, 1);
    } else {
      // Customer is not selected, add it (select)
      updatedSelection.push({
        ...customer,
        fkCustomerId: customer.id,
      });
    }
    setSelectedCustomers(updatedSelection);
  };

  const handleSelectAll = () => {
    customerData?.forEach((customer: any) => {
      handleCustomerSelection(customer);
    });
  };

  const onSearchSubmit = (data: FormValues) => {
    // Only validate search-specific fields, not the group name
    if (data.keyword && data.keyword.length > 0 && data.keyword.length < 3) {
      toast.error(t("list.validation.searchMinChars"));
      return;
    }
    if (
      createdDateRange[0] &&
      createdDateRange[1] &&
      createdDateRange[0] > createdDateRange[1]
    ) {
      toast.error(t("list.validation.createdFromAfterTo"));
      return;
    }

    // Format date without timezone conversion
    const formatDateForAPI = (date: Date | null, isEnd: boolean = false) => {
      if (!date) return undefined;
      if (isEnd) {
        return (
          moment(date).endOf("day").utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
        );
      }
      return (
        moment(date).startOf("day").utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
      );
    };

    let searchData = {
      keyword:
        data.keyword && data.keyword.trim() !== "" ? data.keyword : undefined,
      city: data.city && data.city.trim() !== "" ? data.city : undefined,
      createdDateFrom: createdDateRange[0]
        ? formatDateForAPI(createdDateRange[0])
        : undefined,
      createdDateTo: createdDateRange[1]
        ? formatDateForAPI(createdDateRange[1], true)
        : undefined,
      locationIds:
        data.locationIds?.map((loc: any) => Number(atob(loc.value))) || [],
      customerGroupIds: data.groupIds?.map((grp: any) => Number(atob(grp.value))) || [],
      status: data.status?.map((s: any) => s.value || s) || [],
    };

    // Reset pagination state for new search
    setCurrentPage(1);
    setHasMore(true);
    searchCustomers(searchData, true);
    setIsInitialSearchDone(true);
  };

  const handleCancel = () => {
    navigationConfirm(
      t("list.leaveDialog.title"),
      t("list.leaveDialog.message"),
      () => navigate(URLS.CUSTOMER_GROUPS),
      confirm
    );
  };

  const handleSaveCustomerGroup = () => {
    // Validate group name
    setCustomerGroupNameError("");
    if (!customerGroupName || customerGroupName.trim().length < 3) {
      setCustomerGroupNameError(
        t("addCustomerGroupManual.validation.groupNameMinLength")
      );
      return;
    }

    if (selectedCustomers?.length == 0) {
      toast.error(t("addCustomerGroupManual.validation.selectAtLeastOneCustomer"));
      return;
    }

    setSubmitting(true);

    const payload = {
      customerGroupId: customergroup?.groupId || null, // Base64 encoded ID, use existing or default
      fkCustomerGroupTypeLookupValueID:
        customergroup?.groupTypeId || "MTQ3", // Default to "1" encoded in base64
      customerGroupName: customerGroupName?.trim(),
      active: customergroup?.active || true,
      customers: selectedCustomers?.map((customer: any) => ({
        customerGroupMapId: customer?.customerGroupMapId || null, // Default to "1" encoded in base64 for new mapping
        fkCustomerId: customer?.fkCustomerId, // Customer ID is already in the correct format
        fkCustomerGroupId: customergroup?.groupId, // Same as customerGroupId
        active: customer?.active || true,
      })),
    };

    if (payload.customers.length === 0) {
      setSubmitting(false);
      toast.error(t("addCustomerGroupManual.validation.selectAtLeastOneCustomerToAdd"));
      return;
    }

    // console.log("Submitted data:", payload);

    customersApi.addUpdateCustomerGroup(
      payload,
      (response: any) => {
        setSubmitting(false);
        if (response?.success) {
          toast.success(
            customergroup
              ? t("addCustomerGroupCustom.toasts.updateSuccess")
              : t("addCustomerGroupCustom.toasts.createSuccess")
          );
          navigate(URLS.CUSTOMER_GROUPS);
        } else {
          toast.error(
            response?.errorMessage || t("addCustomerGroupManual.toasts.saveFailed")
          );
        }
      },
      (error: any) => {
        setSubmitting(false);
        console.error("Error saving customer group:", error);
        toast.error(t("addCustomerGroupManual.toasts.saveError"));
      }
    );
  };

  useEffect(() => {
    setTimeout(() => customerGroupNameRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    // Load customer groups for dropdown - saga will automatically store in Redux
    customersApi.listCustomerGroups(
      {},
      (res: any) => {
        if (!res?.success) {
          toast.error(res?.errorMessage || t("customerGroups.toasts.fetchFailed"));
          return;
        }
      },
      (error: any) => {
        console.log("Customer groups load error:", error);
      }
    );

    commonApi.locationSearch(
      {},
      (res: any) => {
        if (!res?.success) {
          toast.error(res?.errorMessage || t("addCustomerGroupManual.toasts.fetchLocationsFailed"));
          return;
        }
      },
      (error: any) => {
        console.log("Locations load error:", error);
      }
    );
    // Only search customers on initial load, don't reset the form
    // searchCustomers();
  }, []);

  useEffect(() => {
    if (customergroup) {
      setCustomerGroupName(customergroup?.groupName || "");
      setSelectedCustomers(
        (customergroup?.members || []).map((cust: any) => ({
          ...cust,
        }))
      );
      
      // Pre-select the current group in the filter dropdown for edit mode
      if (customergroup?.groupId) {
        setValue("groupIds", [{ 
          value: customergroup.groupId, 
          label: customergroup.groupName 
        }]);
        
        // Also search for customers in this group immediately
        const searchData = {
          customerGroupIds: [Number(atob(customergroup.groupId))]
        };
        setCurrentPage(1);
        setHasMore(true);
        searchCustomers(searchData, true);
        setIsInitialSearchDone(true);
      }
    } else {
      // In create mode, don't do initial search, wait for user to search
      setIsInitialSearchDone(false);
    }
  }, [customergroup, setValue]);

  // Remove the old pagination effect since we're using infinite scroll
  // useEffect(() => {
  //   // Only search on pagination change if not in edit mode or if no customergroup is provided
  //   if (!customergroup) {
  //     searchCustomers(undefined, false);
  //   } else {
  //     // In edit mode, search with the customer group filter
  //     const searchData = {
  //       customerGroupIds: [Number(atob(customergroup.groupId))]
  //     };
  //     searchCustomers(searchData, false);
  //   }
  // }, [currentPage, pageSize, customergroup]);

  return (
    <div>
      <SubHeader
        title={t("addCustomerGroupManual.page.title")}
        subTitle={customergroup ? t("addCustomerGroupManual.page.editSubtitle") : t("addCustomerGroupManual.page.createSubtitle")}
      >
        <SiteButtons
          data={[
            { text: t("addCustomerGroupCustom.buttons.cancel"), 
              ...(!submitting && { click: handleCancel }),
              theme: "secondary" },
            {
              text: customergroup ? t("addCustomerGroupCustom.buttons.update") : t("addCustomerGroupCustom.buttons.save"),
              click: handleSaveCustomerGroup,
              working: submitting,
            },
          ]}
        />
      </SubHeader>
      <Container className="mb-12">
        <form
          onSubmit={handleSubmit(onSearchSubmit)}
          id="createCustomerGroupManualForm"
        >
          <BodyGroupHeading title={t("addCustomerGroupManual.sections.groupDetails")} />
          <div className="grid grid-cols-3 gap-4 mb-20 mt-6">
            <Input
              ref={customerGroupNameRef}
              type="text"
              className="!my-0"
              placeholder={t("addCustomerGroupManual.placeholders.enterGroupName")}
              label={t("addCustomerGroupManual.fields.groupName")}
              value={customerGroupName}
              onChange={(e) => {
                setCustomerGroupName(e.target.value);
                if (customerGroupNameError) {
                  setCustomerGroupNameError("");
                }
              }}
              error={customerGroupNameError}
            />
          </div>

          <BodyGroupHeading title={t("addCustomerGroupManual.sections.searchAndSelectCustomers")} />
          <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4 my-6">
              <div className="col-span-2">
                <Controller
                  name="keyword"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className="!my-0"
                      placeholder={t("list.placeholders.searchCustomers")}
                      label={t("list.fields.searchForCustomers")}
                    >
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 mt-1">
                        <Icon name="search" />
                      </span>
                    </Input>
                  )}
                />
              </div>
              <div className="col-span-2">
                <CommonDropdown
                  control={control}
                  setValue={setValue}
                  label={t("list.fields.customerGroup")}
                  name="groupIds"
                  type="allCustomerGroups"
                  placeholder={t("list.placeholders.all")}
                  className="!my-0"
                  isMulti={true}
                />
              </div>

              <div>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className="!my-0"
                      placeholder={t("list.placeholders.enterCity")}
                      label={t("list.fields.city")}
                    />
                  )}
                />
              </div>

              <div>
                <CommonDropdown
                  control={control}
                  setValue={setValue}
                  label={t("list.fields.location")}
                  name="locationIds"
                  className="!my-0"
                  type="allLocations"
                  placeholder={t("list.placeholders.all")}
                  isMulti={true}
                />
              </div>

              {/* Status */}
              <div>
                <CommonDropdown
                  name="status"
                  control={control}
                  setValue={setValue}
                  label={t("list.fields.status")}
                  placeholder={t("list.placeholders.all")}
                  className="!my-0"
                  type="lookup"
                  data={[
                    {
                      lookupId: "active",
                      lookupValue: t("common.values.active"),
                      lookupType: "customerStatus",
                    },
                    {
                      lookupId: "inactive",
                      lookupValue: t("common.values.inactive"),
                      lookupType: "customerStatus",
                    },
                  ]}
                  isMulti={true}
                />
              </div>

              {/* Created Date From */}
              <div className="w-full !my-0">
                <style>
                  {`
                    .react-datepicker-wrapper,
                    .react-datepicker__input-container,
                    .react-datepicker__input-container input {
                        width: 100% !important;
                        display: block !important;
                    }
                    .react-datepicker__close-icon::after {
                        background-color: transparent !important;
                        color: #b6b5b5 !important;
                        font-size: 20px;
                    }
                    .react-datepicker__close-icon:hover::after {
                        background-color: transparent !important;
                        color: #b6b5b5 !important;
                        font-size: 20px;
                    }
                  `}
                </style>
                <p className="font-normal text-[0.938rem] leading-none mb-2">
                  {t("addCustomerGroupManual.fields.dateCreatedFrom")}
                </p>
                <DatePicker
                  selected={createdDateRange[0]}
                  showYearDropdown
                  dropdownMode="select"
                  showMonthDropdown
                  onChange={(date: Date | null) => {
                    setCreatedDateRange([date, createdDateRange[1]]);
                  }}
                  minDate={new Date("2020-01-01")}
                  maxDate={createdDateRange[1] || new Date()}
                  dateFormat="MM/dd/yyyy"
                  className="w-full px-3 h-[2.875rem] py-[8px] border border-[#cecece] rounded-md focus:outline-none"
                  placeholderText={t("addCustomerGroupManual.placeholders.selectStartDate")}
                  isClearable={true}
                />
                {errors.createdDateFrom?.message && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.createdDateFrom.message}
                  </p>
                )}
              </div>

              {/* Created Date To */}
              <div className="w-full !my-0">
                <p className="font-normal text-[0.938rem] leading-none mb-2">
                  {t("addCustomerGroupManual.fields.dateCreatedTo")}
                </p>
                <DatePicker
                  selected={createdDateRange[1]}
                  showYearDropdown
                  dropdownMode="select"
                  showMonthDropdown
                  onChange={(date: Date | null) => {
                    setCreatedDateRange([createdDateRange[0], date]);
                  }}
                  minDate={createdDateRange[0] || new Date("2020-01-01")}
                  maxDate={new Date()}
                  dateFormat="MM/dd/yyyy"
                  className="w-full px-3 h-[2.875rem] py-[8px] border border-[#cecece] rounded-md focus:outline-none"
                  placeholderText={t("addCustomerGroupManual.placeholders.selectEndDate")}
                  isClearable={true}
                />
                {errors.createdDateTo?.message && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.createdDateTo.message}
                  </p>
                )}
              </div>
            </div>

            <SiteButtons
              data={[
                {
                  text: t("list.buttons.search"),
                  type: "submit",
                  working: searching, 
                  theme: "secondary"
                },
                { text: t("list.buttons.reset"), click: () => resetSearch(), theme: "secondary" },
              ]}
            />
          </div>
          {isInitailSearchDone || customergroup ? (
            <>
              {/* Customer Selection Table */}
              <BodyGroupHeading
                subtitle={t("addCustomerGroupManual.selection.summary", {
                  count: totalRows,
                  selected: selectedCustomers?.length || 0,
                  showing: Math.min(customerData?.length || 0, totalRows),
                  total: totalRows,
                })}
                showHr={false}
                className="flex items-center justify-between"
              />
              <div className="bg-white border border-[#043E6133] rounded-[5px] p-[5px] max-h-[600px] overflow-hidden">
                <div className="overflow-y-auto max-h-[580px]" onScroll={handleScroll}>
                  <table className="w-full">
                    {/* Fixed Header */}
                    <thead className="bg-[#e3e4e9] sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-left">
                          
                        </th>
                        <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-left">
                          {t("list.columns.customer")}
                        </th>
                        <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-left">
                          {t("list.fields.location")}
                        </th>
                        <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-left">
                          {t("list.columns.lastVisit")}
                        </th>
                        <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-left">
                          {t("list.columns.storeCredit")}
                        </th>
                        <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-left">
                          {t("list.columns.loyaltyPoints")}
                        </th>
                        <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-left">
                          {t("list.fields.status")}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {customerData && customerData.length > 0 ? (
                        customerData.map((item: any) => (
                          <tr key={item.id} className="border-b border-[#F0F0F0]">
                            {/* Selection Checkbox */}
                            <td className="px-4 py-3 text-sm">
                              <div className="relative flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={
                                    selectedCustomers.find(
                                      (c) => c.fkCustomerId === item.id
                                    ) !== undefined
                                  }
                                  onChange={() => handleCustomerSelection(item)}
                                  className="w-4 h-4 cursor-pointer"
                                />
                              </div>
                            </td>

                            {/* Customer Info */}
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-3 text-black">
                                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 overflow-hidden">
                                  {item.photoUrl ? (
                                    <img
                                      src={item.photoUrl}
                                      alt={t("common.values.avatarAlt")}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : item.firstName ? (
                                    item.firstName.charAt(0).toUpperCase()
                                  ) : (
                                    "G"
                                  )}
                                </div>
                                <div>
                                  <div className="font-medium">
                                    {item.firstName || item.lastName
                                      ? `${item.businessName ? item.businessName + ", " : ""}${item.firstName || ""} ${item.lastName || ""}`.trim()
                                      : `${item.businessName ? item.businessName + ", " : ""}${t("list.values.guest")}`.trim()}
                                  </div>
                                  <div className="border-b-2 border-black my-1"></div>
                                  <div className="text-sm text-gray-500">
                                    {formatPhoneNumber(item.mobileAtob)}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Location */}
                            <td className="px-4 py-3 text-sm">
                              {item.locations
                                ?.map((loc: any) => loc.locationName || "")
                                .join(", ") || "-"}
                            </td>

                            {/* Last Visit */}
                            <td className="px-4 py-3 text-sm">
                                <FormattedDate date={item.modifyDate} format="dateTime" />
                            </td>

                            {/* Store Credit */}
                            <td className="px-4 py-3 text-sm">
                              {item.storeCredit || "0.00"}
                            </td>

                            {/* Loyalty Points */}
                            <td className="px-4 py-3 text-sm">
                              {item.loyalty || "0.00"}
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3 text-sm">
                              {item.active ? (
                                <div className="flex items-center gap-2">
                                  <span className="h-2 w-2 bg-green-600 rounded-full text-xs font-medium"></span>
                                  <p>{t("common.values.active")}</p>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className="h-2 w-2 bg-red-600 rounded-full text-xs font-medium"></span>
                                  <p>{t("common.values.inactive")}</p>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : searching ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-3 text-sm text-center">
                            <div className="flex justify-center items-center">
                              <Loader />
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr>
                          <td colSpan={7} className="px-4 py-3 text-sm text-center">
                            {t("addCustomerGroupManual.values.noDataAvailable")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Loading More Indicator */}
                  {loadingMore && (
                    <div className="flex justify-center items-center py-4">
                      {/* <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Loading more customers...</span> */}
                      <Loader />
                    </div>
                  )}

                  {/* No More Data Indicator */}
                  {/* {!hasMore && customerData?.length > 0 && (
                    <div className="text-center py-4 text-gray-500">
                      No more customers to load
                    </div>
                  )} */}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center bg-white p-6 mt-12 rounded-md border border-dashed border-gray-300">
              <p className="text-gray-500">
                {t("addCustomerGroupManual.emptyState.message")}
              </p>
            </div>
          )}
        </form>
      </Container>
    </div>
  );
};

export default AddCustomerGroupManual;
